import { FAQ_SECTIONS } from '../../constants/faqData';
import { MILLET_SITE_URL } from '../../constants/milletAskAiLinks';
import { MILLET_SOCIAL_LINKS } from '../../constants/milletSocialLinks';
import MILLET_PRICING_PLANS from '../../pages/Payment/pricingPlans';
import { getAllBlogPosts, getBlogPostBySlug, getBlogPostSlug } from '../blog/blogData';

const HOME_SUMMARY = 'Millet is a live shopping marketplace to find creators. Brands find creators, compare rates, run collaborations, and track shows and payouts. Creators build a profile, take live and short-video collabs, and manage invites and pay.';

const START_HERE = [
  ['Home', '/'],
  ['Browse influencers', '/marketplace/influencers'],
  ['Influencer map', '/creator-map'],
  ['Browse campaigns', '/marketplace/campaigns'],
  ['Pricing', '/pricing'],
  ['FAQ', '/faq'],
  ['Blog', '/blog'],
  ['Contact', '/contact-us'],
  ['Privacy Policy', '/privacy-policy'],
  ['Terms of Service', '/terms-and-conditions'],
];

const yamlString = (value) => `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

export const isAgentMarkdownPath = (pathname) => String(pathname || '').endsWith('.md');

export const humanPathFromAgentPath = (pathname) => {
  if (!isAgentMarkdownPath(pathname)) return null;
  const stripped = String(pathname).slice(0, -3);
  if (stripped === '' || stripped === '/') return '/';
  return stripped.endsWith('/') ? stripped.slice(0, -1) : stripped;
};

/** Same-origin URL Vercel opens: the current path with `.md` appended. */
export const agentMarkdownHref = (pageUrl) => {
  const url = new URL(pageUrl, MILLET_SITE_URL);
  const path = url.pathname.replace(/\/$/, '');
  if (!path.endsWith('.md')) {
    url.pathname = `${path}.md`;
  }
  return url.href;
};

const agentLink = (path) => {
  if (path === '/') return '/.md';
  return `${path}.md`;
};

const startHereMarkdown = () => [
  '## Start here',
  '',
  ...START_HERE.map(([label, path]) => `- [${label}](${agentLink(path)})`),
].join('\n');

const frontmatter = ({ title, url, summary }) => [
  '---',
  `title: ${yamlString(title)}`,
  `url: ${url}`,
  `summary: ${yamlString(summary)}`,
  '---',
  '',
].join('\n');

const htmlToMarkdown = (html) => String(html || '')
  .replace(/<h2[^>]*>/gi, '\n\n## ')
  .replace(/<h3[^>]*>/gi, '\n\n### ')
  .replace(/<\/h[1-6]>/gi, '\n')
  .replace(/<li[^>]*>/gi, '\n- ')
  .replace(/<\/p>/gi, '\n\n')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/&apos;|&#39;/g, "'")
  .replace(/&quot;/g, '"')
  .replace(/&amp;/g, '&')
  .replace(/&nbsp;/g, ' ')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

const documentFor = ({ title, url, summary, body }) => [
  frontmatter({ title, url, summary }),
  `# ${title}`,
  '',
  body.trim(),
  '',
].join('\n');

const homeDocument = (origin) => documentFor({
  title: 'Millet',
  url: '/',
  summary: HOME_SUMMARY,
  origin,
  body: [
    'Build the next iconic brand with creators.',
    '',
    HOME_SUMMARY,
    '',
    startHereMarkdown(),
  ].join('\n'),
});

const pricingDocument = (origin) => {
  const plans = MILLET_PRICING_PLANS.map((plan) => {
    const features = plan.features.map((feature) => `  - ${feature}`).join('\n');
    return `- **${plan.displayName}** — ${plan.monthlyPrice} ${plan.priceSubtext}. ${plan.tagline}\n${features}`;
  }).join('\n');
  return documentFor({
    title: 'Pricing',
    url: '/pricing',
    summary: 'Plans for inviting creators and running collaborations on Millet.',
    origin,
    body: [
      'See plans for inviting creators and running collaborations.',
      '',
      plans,
      '',
      'Canonical page: /pricing',
    ].join('\n'),
  });
};

const faqDocument = (origin) => {
  const sections = FAQ_SECTIONS.map((section) => {
    const items = section.items.map((item) => `### ${item.question}\n\n${item.answer}`).join('\n\n');
    return `## ${section.title}\n\n${items}`;
  }).join('\n\n');
  return documentFor({
    title: 'FAQ',
    url: '/faq',
    summary: 'Answers about Millet for brands and creators.',
    origin,
    body: sections,
  });
};

const blogIndexDocument = (origin) => {
  const posts = getAllBlogPosts().map((post) => {
    const slug = getBlogPostSlug(post);
    return `- [${post.title}](/blog/${slug}.md) — ${post.excerpt}`;
  }).join('\n');
  return documentFor({
    title: 'Blog',
    url: '/blog',
    summary: 'Millet articles on live shopping, creators, and brand collaborations.',
    origin,
    body: posts,
  });
};

const blogPostDocument = (slug, origin) => {
  const post = getBlogPostBySlug(slug);
  if (!post) return null;
  const url = `/blog/${getBlogPostSlug(post)}`;
  return documentFor({
    title: post.title,
    url,
    summary: post.excerpt,
    origin,
    body: [
      post.aiSummary,
      '',
      htmlToMarkdown(post.content),
    ].join('\n'),
  });
};

const contactDocument = (origin) => {
  const socials = MILLET_SOCIAL_LINKS.map((link) => `- [${link.name}](${link.href})`).join('\n');
  return documentFor({
    title: 'Contact Us',
    url: '/contact-us',
    summary: 'Contact Millet about the live shopping marketplace.',
    origin,
    body: [
      'Send a message from the contact form on the canonical page: /contact-us',
      '',
      socials,
    ].join('\n'),
  });
};

const legalDocument = ({ title, url, summary, origin }) => documentFor({
  title,
  url,
  summary,
  origin,
  body: [
    summary,
    '',
    `Read the full document on the canonical page: ${url}`,
  ].join('\n'),
});

const listingDocument = ({ title, url, summary, origin }) => documentFor({
  title,
  url,
  summary,
  origin,
  body: [summary, '', startHereMarkdown()].join('\n'),
});

const fallbackDocument = (url, origin) => documentFor({
  title: 'Millet',
  url,
  summary: HOME_SUMMARY,
  origin,
  body: [
    `This address does not have its own agent document yet.`,
    '',
    `Canonical page: ${url}`,
    '',
    HOME_SUMMARY,
    '',
    startHereMarkdown(),
  ].join('\n'),
});

const STATIC_PAGES = {
  '/marketplace/influencers': {
    title: 'Browse influencers',
    summary: 'Find creators by niche, rate, and platform.',
  },
  '/marketplace/campaigns': {
    title: 'Browse campaigns',
    summary: 'Browse live campaigns that are hiring creators.',
  },
  '/creator-map': {
    title: 'Influencer map',
    summary: 'See which creators are near a market.',
  },
  '/sign-up': {
    title: 'Sign up',
    summary: 'Create a free Millet account as a brand or a creator.',
  },
  '/signup': {
    title: 'Sign up',
    summary: 'Create a free Millet account as a brand or a creator.',
  },
  '/login': {
    title: 'Log in',
    summary: 'Log in to Millet.',
  },
};

export const buildAgentMarkdown = (pathname, { origin = MILLET_SITE_URL } = {}) => {
  const humanPath = humanPathFromAgentPath(pathname) || pathname || '/';
  const siteOrigin = String(origin || MILLET_SITE_URL).replace(/\/$/, '');

  if (humanPath === '/') return homeDocument(siteOrigin);
  if (humanPath === '/pricing') return pricingDocument(siteOrigin);
  if (humanPath === '/faq') return faqDocument(siteOrigin);
  if (humanPath === '/blog') return blogIndexDocument(siteOrigin);
  if (humanPath === '/contact-us') return contactDocument(siteOrigin);
  if (humanPath === '/privacy-policy' || humanPath === '/legal/privacy') {
    return legalDocument({
      title: 'Privacy Policy',
      url: '/privacy-policy',
      summary: 'How Millet collects, uses, and protects personal information across its creator and brand marketplace.',
      origin: siteOrigin,
    });
  }
  if (humanPath === '/terms-and-conditions' || humanPath === '/legal/terms') {
    return legalDocument({
      title: 'Terms of Service',
      url: '/terms-and-conditions',
      summary: 'The Terms of Service that govern access to and use of Millet for brands, creators, campaigns, collaborations, and payments.',
      origin: siteOrigin,
    });
  }

  const blogMatch = humanPath.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    const post = blogPostDocument(blogMatch[1], siteOrigin);
    if (post) return post;
  }

  const listing = STATIC_PAGES[humanPath];
  if (listing) {
    return listingDocument({
      title: listing.title,
      url: humanPath,
      summary: listing.summary,
      origin: siteOrigin,
    });
  }

  return fallbackDocument(humanPath, siteOrigin);
};
